/* globals __DEV__ */
/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import css from '@emotion/css/macro'
import { Route, Switch } from 'react-router-dom'
import { connect } from 'react-redux'
import { push } from 'connected-react-router'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import gameActions from '@sportninja/common/actions/game'
import orgActions from '@sportninja/common/actions/org'
import { makeGetGame } from '@sportninja/common/selectors/game'
import { t } from '@sportninja/common/i18n'
import { useReadWithPagination } from '@sportninja/common/utils/utils'
import { ROUTES } from '@sportninja/common/constants/app'
import {
  getGameTypesForHockey,
  getGameTypesForSoccer,
  getHockeySport,
  getSoccerSport,
} from '@sportninja/common/selectors/types'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'

import { gameForm } from '../../components/Form/form-configs'
import PageWrap from '../../components/PageWrap'
import FormSheet from '../../components/Form/FormSheet'
import { backgroundColorLight } from '../../components/css'
import { Desktop, Mobile } from '../../components/Responsive'
import Highlights from '../../components/Highlights'
import { Column, Container } from '../../components/Layout'
import { createSubPages } from '../utils'
import EditEntitySubTitle from '../EditEntitySubTitle'
import Details from './Details'
import Timeline from './Timeline'
import GamePageHeader from './GamePageHeader'
import Scoresheet from './Scoresheet'
import GameRoster from './GameRoster'
import PlayerStatistics from './PlayerStatistics'
import BambuserPlayer from './BambuserPlayer'
import DevSignatureList from './DevSignatureList'
import LiveVideo from './LiveVideo'

const Game = ({
  isPublicRoute,
  backToParentSchedule,
  game,
  gameTypes,
  inFrame,
  match: {
    params: { id },
  },
  readGame,
  updateGame,
  deleteGame,
  readPublicGame,
  readOrg,
  isSoccer = false,
}) => {
  const [disableTabNavigation, setDisableTabNavigation] = useState(false)
  const [ifNecessaryStatus, setIfNecessaryStatus] = useState(false)

  const hasPermission = game.permission?.update

  const PAGES = {
    details: t('common:details'),
    'home-roster': t('Web:homeRoster'),
    'visitor-roster': t('Web:visitorRoster'),
  }

  if (!isPublicRoute) PAGES.highlights = 'Highlights'

  if (!isPublicRoute && game.completed) PAGES.statistics = t('Web:statistics')

  if (game.started) {
    PAGES.timeline = t('routeNames:timeline')
  }

  PAGES.watch = 'Watch'

  if (!isPublicRoute) PAGES.scoresheet = t('Web:scoresheet')

  if (__DEV__) PAGES.signatures = 'Signatures'

  const [subPages, routes] = createSubPages(
    isPublicRoute ? ROUTES.PUBLIC_GAME_ROOT : ROUTES.GAME_ROOT,
    id,
    PAGES,
    {
      hasPermission: hasPermission || game.permission?.admin,
      privatePageCount: isPublicRoute ? 0 : 1,
    }
  )
  const [loaded, error] = useReadWithPagination(
    isPublicRoute ? readPublicGame : readGame,
    id
  )

  const actions = [
    game && hasPermission && (
      <Route
        key='edit'
        exact
        path={routes.details}
        render={() => (
          <FormSheet
            key='edit-game'
            icon='pencil-alt'
            form={gameForm({
              ...game,
              ifNecessaryStatus,
              onSelectIfNecessary: () =>
                setIfNecessaryStatus(!ifNecessaryStatus),
            })}
            label={t('Web:editGame')}
            onDelete={
              game.permission?.delete && !game?.rootSchedule?.is_tournament
                ? async (...args) => {
                    await deleteGame(game.parentId, game.id, ...args)
                    backToParentSchedule(game.rootSchedule?.id)
                  }
                : undefined
            }
            onSubmit={async (...args) => {
              try {
                const obj =
                  args.length === 1
                    ? args[0]
                    : args.find((el) => el.if_necessary_status !== undefined)
                const rest = args.filter((el) => el !== obj)
                const result = [
                  ...rest,
                  {
                    ...obj,
                    venue_id: obj?.venue_id ?? null,
                    if_necessary_status: ifNecessaryStatus,
                  },
                ]

                return await updateGame(game.id, ...result)
              } finally {
                readGame(game.id)
              }
            }}
            title={t('Web:editGame')}
            subTitle={
              <EditEntitySubTitle id={game.id} entityType={ENTITY_TYPES.game} />
            }
          />
        )}
      />
    ),
  ]

  const fullGameType = gameTypes?.find((gameType) => {
    return parseInt(gameType?.id, 10) === game?.gameType
  })

  useEffect(() => {
    if (game?.ifNecessaryStatus) {
      setIfNecessaryStatus(game.ifNecessaryStatus)
    }
  }, [game?.ifNecessaryStatus])

  const isCanlanVideo = game?.rootSchedule?.id === '1TNsv6KEoPSNtjem'
  const isPlanetIceVideo = game?.rootSchedule?.id === 'pVPyixy0KqSfOJdT'
  const shouldShowWaiverStatus = game?.should_show_waiver

  return (
    <PageWrap
      extraOffset={54}
      error={error}
      loaded={loaded}
      pages={subPages}
      navigationDisabled={disableTabNavigation}
      pageHeader={
        <GamePageHeader
          error={error}
          actions={inFrame ? [] : actions}
          game={game}
          fullGameType={fullGameType}
          inFrame={inFrame}
          organization={
            game?.rootSchedule?.organization_id
              ? {
                  id: game?.rootSchedule?.organization_id,
                  name: game?.organization_name,
                }
              : null
          }
          disabled={disableTabNavigation}
          isSoccer={isSoccer}
        />
      }
    >
      <Mobile>
        {(isMobile) => {
          return (
            <div
              css={
                isMobile &&
                css`
                  position: relative;
                  margin-top: -40px;
                  padding-top: 40px;
                  background-color: ${inFrame
                    ? 'transparent'
                    : backgroundColorLight};
                `
              }
            >
              <Switch>
                <Route
                  exact
                  path={routes.details}
                  render={(props) => (
                    <Details game={game} {...props} isSoccer={isSoccer} />
                  )}
                />

                {game.started && (
                  <Route
                    exact
                    path={routes.timeline}
                    render={(props) => (
                      <Timeline
                        gameId={game.id}
                        isPublicRoute={isPublicRoute}
                        setDisableTabNavigation={setDisableTabNavigation}
                        isSoccer={isSoccer}
                        {...props}
                      />
                    )}
                  />
                )}

                <Route
                  exact
                  path={routes['home-roster']}
                  render={(props) => (
                    <GameRoster
                      isSoccer={isSoccer}
                      key='home'
                      isPublicRoute={isPublicRoute}
                      gameId={game.id}
                      isGameStarted={game.started}
                      rosterId={game.homeGameRosterId}
                      {...props}
                    />
                  )}
                />

                <Route
                  exact
                  path={routes['visitor-roster']}
                  render={(props) => (
                    <GameRoster
                      isSoccer={isSoccer}
                      key='visiting'
                      isPublicRoute={isPublicRoute}
                      gameId={game.id}
                      isGameStarted={game.started}
                      rosterId={game.visitingGameRosterId}
                      {...props}
                    />
                  )}
                />

                {!isPublicRoute && (
                  <Route
                    exact
                    path={routes.scoresheet}
                    render={() => (
                      <Scoresheet gameNumber={game.gameNumber} id={id} />
                    )}
                  />
                )}

                {!isPublicRoute && game.completed && (
                  <Route
                    exact
                    path={routes.statistics}
                    render={() => (
                      <PlayerStatistics
                        id={id}
                        homeTeamId={game.homeTeamId}
                        visitingTeamId={game.visitingTeamId}
                      />
                    )}
                  />
                )}

                <Route
                  exact
                  path={routes.watch}
                  render={() => {
                    if (isCanlanVideo || isPlanetIceVideo) {
                      return (
                        <LiveVideo
                          gameId={game.id}
                          startedAt={game.startedAt}
                          hasCompleted={game.completed}
                        />
                      )
                    }

                    return (
                      <BambuserPlayer
                        game={game}
                        isPublicRoute={isPublicRoute}
                      />
                    )
                  }}
                />

                {__DEV__ && (
                  <Route
                    exact
                    path={routes.signatures}
                    render={() => {
                      return <DevSignatureList game={game} />
                    }}
                  />
                )}

                {!isPublicRoute && (
                  <Route
                    exact
                    path={routes.highlights}
                    render={() => {
                      return (
                        <Container>
                          <Column
                            fullWidth
                            column
                            style={{
                              width: '100%',
                              marginBottom: 40,
                              marginTop: -24,
                            }}
                          >
                            <Highlights requestUrl={`games/${id}`} />
                          </Column>
                        </Container>
                      )
                    }}
                  />
                )}
              </Switch>
            </div>
          )
        }}
      </Mobile>
    </PageWrap>
  )
}

const makeMapStateToProps = () => {
  const getGameById = makeGetGame()

  const mapStateToProps = (state, props) => {
    const game = getGameById(state, props.match.params.id)
    const hockeySportId = getHockeySport(state)?.id
    const soccerSportId = getSoccerSport(state)?.id
    let gameTypes
    let isSoccer = false
    if (game?.sport_id === soccerSportId) {
      gameTypes = getGameTypesForSoccer(state)
      isSoccer = true
    } else {
      gameTypes = getGameTypesForHockey(state)
      isSoccer = false
    }
    return {
      inFrame: state.auth.inFrame,
      game,
      gameTypes,
      isSoccer,
    }
  }

  return mapStateToProps
}

const mapDispatchToProps = (dispatch) => {
  return {
    backToParentSchedule: (scheduleId) =>
      dispatch(push(`${ROUTES.SCHEDULE_ROOT}/${scheduleId}`)),
    readGame: bindActionToPromise(dispatch, gameActions.read.request),
    readPublicGame: bindActionToPromise(
      dispatch,
      gameActions.readPublic.request
    ),
    updateGame: bindActionToPromise(dispatch, gameActions.update.request),
    deleteGame: bindActionToPromise(dispatch, gameActions.delete.request),
    readOrg: bindActionToPromise(dispatch, orgActions.read.request),
  }
}

export default connect(makeMapStateToProps, mapDispatchToProps)(Game)
